import React, { useState, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useOpenAttachmentsPanel } from '../../../../../attachments/use-open-attachments-panel';
import { useWorkspacePanel } from '../../../../../panels/hooks/use-workspace-panel';
import { useBoloPanelMetadata } from '../../../state/bolo-panel-metadata-context';
import {
    AttachmentsView,
    AttachmentsViewHandle,
} from '../../../../../attachments/attachments-view';
import { BoloPanelTab } from '../bolo-panel-tab';
import { TabBody } from '../../../../../panels/entity-panels/tabs/tab-body';
import {
    MAX_NUM_OF_ATTACHMENTS_PER_BOLO,
    DEFAULT_ATTACHMENT_SORT_OPTION_VALUE,
} from '../../../../../attachments/constants';
import { AttachmentsHeaderActions } from '../../../../../attachments/attachments-header-actions';
import { AttachmentSortOptionValue } from '../../../../../attachments/types';
import { useAttachmentsAbilities } from '~/attachments/hooks/use-attachments-abilities';

export const BoloAttachmentsTab = observer(
    function BoloAttachmentsTab(): React.ReactElement | null {
        const workspacePanel = useWorkspacePanel();
        const boloMetadata = useBoloPanelMetadata();
        const entityId = workspacePanel.linkedEntityId ?? boloMetadata?.boloId;
        const openAttachmentsPanel = useOpenAttachmentsPanel();
        const { canAddAttachments } = useAttachmentsAbilities('BOLO');
        const [attachmentCount, setAttachmentCount] = useState(0);
        const [sortOption, setSortOption] = useState<AttachmentSortOptionValue>(
            DEFAULT_ATTACHMENT_SORT_OPTION_VALUE
        );
        const attachmentsViewRef = useRef<AttachmentsViewHandle>(null);

        const isMaxReached = attachmentCount >= MAX_NUM_OF_ATTACHMENTS_PER_BOLO;

        const handleAttachmentCountChange = useCallback((count: number) => {
            setAttachmentCount(count);
        }, []);

        const handleUploadClick = () => {
            if (canAddAttachments) {
                attachmentsViewRef.current?.openFileDialog();
            }
        };

        if (!entityId) {
            return null;
        }

        const handleOpenPreview = (attachmentId: number) => {
            openAttachmentsPanel({
                entityId,
                entityType: 'BOLO',
                shouldFocus: true,
                attachmentId,
                sortOption,
            });
        };

        const titleTrailingNode =
            attachmentCount > 0 ? (
                <AttachmentsHeaderActions
                    onUploadClick={handleUploadClick}
                    isUploadDisabled={!canAddAttachments || isMaxReached}
                    sortOption={sortOption}
                    onSortChange={setSortOption}
                />
            ) : null;

        return (
            <BoloPanelTab
                title={`Attachments${attachmentCount > 0 ? ` (${attachmentCount})` : ''}`}
                titleTrailingNode={titleTrailingNode}
            >
                <TabBody constrainToParentHeight>
                    <AttachmentsView
                        ref={attachmentsViewRef}
                        entityId={entityId}
                        entityType="BOLO"
                        entityNumber={boloMetadata?.boloNumber}
                        onOpenPreview={handleOpenPreview}
                        onAttachmentCountChange={handleAttachmentCountChange}
                        sortOption={sortOption}
                    />
                </TabBody>
            </BoloPanelTab>
        );
    }
);
